export type VoiceFlavorId = 'WARM_FRIENDLY' | 'PROFESSIONAL_CALM' | 'ENERGETIC' | 'DIRECT' | 'BALANCED_NEUTRAL';

export type VoiceType = 'MALE' | 'FEMALE' | 'NEUTRAL';

export interface VoiceFlavorPreset {
  id: VoiceFlavorId;
  displayName: string;
  description: string;
  voiceType: VoiceType;
  voiceGender: 'MALE' | 'FEMALE';
  previewVoice: string;
  previewPrompt: string;
  defaultAttributes: {
    tone: number;
    friendliness: number;
    professionalism: number;
  };
}

export type VoiceAttributeLevel = 'LOW' | 'MEDIUM' | 'HIGH';

export const VOICE_ATTRIBUTE_LEVELS: Record<VoiceAttributeLevel, number> = {
  LOW: 25,
  MEDIUM: 50,
  HIGH: 75,
};

export const FALLBACK_VOICE_FLAVOR_PRESETS: VoiceFlavorPreset[] = [
  {
    id: 'WARM_FRIENDLY',
    displayName: 'Warm & Friendly',
    description: 'Approachable and conversational. Ideal for service scheduling and customer care.',
    voiceType: 'FEMALE',
    voiceGender: 'FEMALE',
    previewVoice: 'nova',
    previewPrompt: 'Speak in a warm, friendly, and approachable tone. Sound helpful and welcoming.',
    defaultAttributes: { tone: 70, friendliness: 85, professionalism: 60 },
  },
  {
    id: 'PROFESSIONAL_CALM',
    displayName: 'Professional & Calm',
    description: 'Steady and reassuring. Best for premium brands and formal customer interactions.',
    voiceType: 'MALE',
    voiceGender: 'MALE',
    previewVoice: 'onyx',
    previewPrompt: 'Speak in a calm, professional, and confident tone. Be clear and reassuring.',
    defaultAttributes: { tone: 45, friendliness: 55, professionalism: 90 },
  },
  {
    id: 'ENERGETIC',
    displayName: 'Energetic',
    description: 'Upbeat and engaging. Works well for promotions and high-energy dealerships.',
    voiceType: 'FEMALE',
    voiceGender: 'FEMALE',
    previewVoice: 'shimmer',
    previewPrompt: 'Speak with upbeat energy and enthusiasm while staying clear and professional.',
    defaultAttributes: { tone: 85, friendliness: 80, professionalism: 55 },
  },
  {
    id: 'DIRECT',
    displayName: 'Direct',
    description: 'Concise and efficient. Good for quick service routing and transactional calls.',
    voiceType: 'MALE',
    voiceGender: 'MALE',
    previewVoice: 'alloy',
    previewPrompt: 'Speak directly and efficiently. Keep responses concise and action-oriented.',
    defaultAttributes: { tone: 40, friendliness: 45, professionalism: 75 },
  },
  {
    id: 'BALANCED_NEUTRAL',
    displayName: 'Balanced & Neutral',
    description: 'Clear and even-toned. A versatile default that works across many dealer brands.',
    voiceType: 'NEUTRAL',
    voiceGender: 'FEMALE',
    previewVoice: 'sage',
    previewPrompt: 'Speak in a balanced, neutral, and easy-to-understand tone. Stay natural and steady.',
    defaultAttributes: { tone: 50, friendliness: 60, professionalism: 65 },
  },
];

export function voiceAttributeLevelFromValue(value?: number): VoiceAttributeLevel {
  if (value === undefined || value === null) {
    return 'MEDIUM';
  }
  if (value <= 35) {
    return 'LOW';
  }
  if (value >= 65) {
    return 'HIGH';
  }
  return 'MEDIUM';
}

/** Convert UI LOW/MEDIUM/HIGH selects into 0–100 scores for the onboarding API. */
export function voiceAttributeScore(level?: string | null): number {
  if (level === 'LOW' || level === 'MEDIUM' || level === 'HIGH') {
    return VOICE_ATTRIBUTE_LEVELS[level];
  }
  return 50;
}

/** Numeric voice-flavor fields sent through the onboarding API payload. */
export interface VoiceFlavorSetupPayload {
  voiceFlavor: string;
  voiceTone: number;
  voiceFriendliness: number;
  voiceProfessionalism: number;
}
